import { useEffect, useMemo, useRef, useState } from 'react';

import { removeEmptyBlocksFromHtml } from '@/features/EditAnalysis/utils/removeEmptyEditorBlocks';

import { EditorWrapper, ToolbarStickyWrap } from './WysiwygEditor.styles';
import { WysiwygEditorProps } from './WysiwygEditor.types';

type ReactQuillInstance = InstanceType<typeof import('react-quill-new').default>;

type LoadedQuill = {
  ReactQuill: typeof import('react-quill-new').default;
  keyboardBindings: { [key: string]: unknown };
};

let quillPromise: Promise<LoadedQuill> | null = null;

const loadQuill = (): Promise<LoadedQuill> => {
  if (!quillPromise) {
    quillPromise = (async () => {
      const [reactQuillModule, tableModule] = await Promise.all([
        import('react-quill-new'),
        import('quill-table-better'),
      ]);

      const ReactQuill = reactQuillModule.default;
      const Quill = reactQuillModule.Quill;
      const QuillTableBetter = tableModule.default;

      Quill.register({ 'modules/table-better': QuillTableBetter }, true);

      return {
        ReactQuill,
        keyboardBindings: QuillTableBetter.keyboardBindings as { [key: string]: unknown },
      };
    })();
  }

  return quillPromise;
};

export const WysiwygEditor = ({
  value = '',
  placeholder = 'Start typing...',
  onChange,
}: WysiwygEditorProps) => {
  const [loaded, setLoaded] = useState<LoadedQuill | null>(null);
  const quillRef = useRef<ReactQuillInstance | null>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    let active = true;

    void loadQuill().then(result => {
      if (active) setLoaded(result);
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!loaded || initializedRef.current || !value) return;

    const editor = quillRef.current?.getEditor();

    if (!editor) return;

    initializedRef.current = true;
    editor.clipboard.dangerouslyPasteHTML(0, value);
  }, [loaded, value]);

  const modules = useMemo(() => {
    if (!loaded) return undefined;

    return {
      toolbar: {
        container: '#wysiwyg-toolbar',
      },
      table: false,
      'table-better': {
        language: 'en_US',
        menus: ['column', 'row', 'merge', 'table', 'cell', 'wrap', 'copy', 'delete'],
        toolbarTable: true,
      },
      keyboard: {
        bindings: loaded.keyboardBindings,
      },
    };
  }, [loaded]);

  const ReactQuill = loaded?.ReactQuill;

  return (
    <EditorWrapper>
      <ToolbarStickyWrap>
        <div id="wysiwyg-toolbar">
          <select
            className="ql-header"
            defaultValue=""
          >
            <option value="2">Header</option>
            <option value="3">Subheader</option>
            <option value="">Normal</option>
          </select>
          <span
            aria-hidden="true"
            className="ql-divider"
          />
          <button
            className="ql-bold"
            type="button"
          />
          <button
            className="ql-italic"
            type="button"
          />
          <button
            className="ql-strike"
            type="button"
          />
          <span
            aria-hidden="true"
            className="ql-divider"
          />
          <button
            className="ql-align"
            type="button"
            value=""
          />
          <button
            className="ql-align"
            type="button"
            value="center"
          />
          <button
            className="ql-align"
            type="button"
            value="right"
          />
          <button
            className="ql-align"
            type="button"
            value="justify"
          />
          <span
            aria-hidden="true"
            className="ql-divider"
          />
          <button
            className="ql-list"
            type="button"
            value="bullet"
          />
          <button
            className="ql-list"
            type="button"
            value="ordered"
          />
          <span
            aria-hidden="true"
            className="ql-divider"
          />
          <button
            className="ql-link"
            type="button"
          />
          <span
            aria-hidden="true"
            className="ql-divider"
          />
          <button
            className="ql-table-better"
            type="button"
            aria-label="Insert table"
          >
            <svg viewBox="0 0 18 18">
              <rect
                className="ql-stroke"
                height="12"
                width="12"
                x="3"
                y="3"
                fill="none"
              />
              <line
                className="ql-stroke"
                x1="3"
                y1="7"
                x2="15"
                y2="7"
              />
              <line
                className="ql-stroke"
                x1="3"
                y1="11"
                x2="15"
                y2="11"
              />
              <line
                className="ql-stroke"
                x1="7"
                y1="3"
                x2="7"
                y2="15"
              />
              <line
                className="ql-stroke"
                x1="11"
                y1="3"
                x2="11"
                y2="15"
              />
            </svg>
          </button>
        </div>
      </ToolbarStickyWrap>
      {ReactQuill && (
        <ReactQuill
          ref={quillRef}
          theme="snow"
          useSemanticHTML={false}
          onChange={nextValue => {
            const str = typeof nextValue === 'string' ? nextValue : value;

            onChange?.(str);
          }}
          onBlur={(_range, _source, editor) => {
            const html = editor?.getHTML?.() ?? '';
            const sanitized = removeEmptyBlocksFromHtml(html);

            if (sanitized !== value) onChange?.(sanitized);
          }}
          placeholder={placeholder}
          modules={modules}
        />
      )}
    </EditorWrapper>
  );
};
